import { isEmpty } from "@swiggy-private/js-utils";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { ICartItemList } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/CartItemList/types";
import type { ILocalCart } from "@swiggy-private/portal-dineout-context";
import { WidgetTypes } from "@swiggy-private/portal-dineout-ui";
import { PersistLocalCartOptions, WebStorageLocation } from "types";

import { CART_NATIVE_BRIDGE_FLOW_TYPE, NR_TABLES, STORAGE_KEYS } from "../constants";
import { deleteFromStorage, getBridgeSdk, getFromStorage, saveToStorage } from "../utils";
import { getCardDataByType } from "../utils/menu";
import { sendCustomNREvViaBlaze } from "../utils/newrelic";

type SwiggyHeaders = Record<string, string | undefined> | undefined;

export type FetchCartResponse = {
    cards: IPLWidgetControllerCards[];
    isChildOrderExist?: boolean | null;
};

export const fetchLocalCart = async (
    isV2InApp: boolean,
    swiggyHeaders: SwiggyHeaders,
): Promise<Record<string, unknown>> => {
    let localCart = {};

    if (isV2InApp) {
        try {
            const sdk = getBridgeSdk();
            const nativeCart = await sdk.dineoutGetLocalCart({ flowType: CART_NATIVE_BRIDGE_FLOW_TYPE });
            if (nativeCart) {
                const serialized = JSON.stringify(nativeCart);
                saveToStorage(STORAGE_KEYS.LOCAL_CART, serialized, WebStorageLocation.SESSION);
                // saveToStorage writes to native bridge storage in-app, not web sessionStorage.
                // Sync web sessionStorage so manageSpecialInstructions/updateCartWithAction read fresh data.
                sessionStorage.setItem(STORAGE_KEYS.LOCAL_CART, serialized);
                localCart = nativeCart;
            }
        } catch {
            sendCustomNREvViaBlaze(NR_TABLES.NATIVE_BRIDGE_FETCH_CART_ERROR, {}, swiggyHeaders ?? {});
        }
    }

    if (isEmpty(localCart)) {
        try {
            const storageResponse = await getFromStorage(STORAGE_KEYS.LOCAL_CART, WebStorageLocation.SESSION, true);
            localCart = JSON.parse(storageResponse?.data?.[STORAGE_KEYS.LOCAL_CART] || "{}") || {};
        } catch (_) {
            sendCustomNREvViaBlaze(NR_TABLES.SESSION_STORAGE_FETCH_CART_ERROR, {}, swiggyHeaders ?? {});
        }
    }

    return localCart;
};

export const hasCartItems = (cart: Record<string, unknown>): boolean => {
    return !isEmpty(cart) && !isEmpty((cart as { cartItems?: unknown[] })?.cartItems);
};

/**
 * Filters local cart items to only keep items still present in the CartInfo widget.
 * BE removes OOS items from CartInfo — this syncs the local cart accordingly.
 * If CartInfo widget is absent, all items are OOS — returns empty cartItems.
 */
export const filterAvailableCartItems = (cards: IPLWidgetControllerCards[], localCart: ILocalCart): ILocalCart => {
    const widget = getCardDataByType<ICartItemList>(cards, WidgetTypes.CART_ITEM_LIST);

    if (isEmpty(widget)) {
        return { ...localCart, cartItems: [] };
    }

    const dishes = widget.cartInfo?.dishes ?? [];
    const availableIds = new Set(dishes.map((d) => String(d.info?.id)));
    const items = localCart.cartItems ?? [];
    const filtered = items.filter((i) => availableIds.has(String(i.menu_item_id)));

    return { ...localCart, cartItems: filtered };
};

/**
 * BE sends CartUnavailableItemsFooter widget when OOS items exist — this is the authoritative signal.
 */
export const hasOOSItems = (cards: IPLWidgetControllerCards[]): boolean => {
    const widget = getCardDataByType(cards, WidgetTypes.CART_UNAVAILABLE_ITEMS_FOOTER);
    return !isEmpty(widget);
};

export const persistLocalCart = (
    syncedCart: ILocalCart,
    cards: IPLWidgetControllerCards[],
    options: PersistLocalCartOptions,
): void => {
    const { updateLocalCartInNative, clearLocalCartInNative, refreshMenuInNative } = options;

    if (isEmpty(syncedCart?.cartItems)) {
        deleteFromStorage(STORAGE_KEYS.LOCAL_CART, WebStorageLocation.SESSION, true);
        clearLocalCartInNative();
    } else {
        const serialized = JSON.stringify(syncedCart);
        try {
            saveToStorage(STORAGE_KEYS.LOCAL_CART, serialized, WebStorageLocation.SESSION);
            // saveToStorage writes to native bridge storage in-app, not web sessionStorage.
            // Sync web sessionStorage so manageSpecialInstructions/updateCartWithAction read fresh data.
            sessionStorage.setItem(STORAGE_KEYS.LOCAL_CART, serialized);
        } catch (_) {}
        updateLocalCartInNative(syncedCart);
    }

    if (hasOOSItems(cards)) {
        refreshMenuInNative();
    }
};
